import React from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import { useUpdateUserRole } from '../../data/useAccountUsers';

const ROLE_LABELS: Record<string, string> = {
  viewer: 'Viewer',
  creator: 'Creator',
  'account-admin': 'Admin',
};

interface ChangeRoleDialogProps {
  open: boolean;
  onClose: () => void;
  accountId: string | undefined;
  userId: string;
  displayName: string;
  currentRole: string;
}

const ChangeRoleDialog: React.FC<ChangeRoleDialogProps> = ({
  open,
  onClose,
  accountId,
  userId,
  displayName,
  currentRole,
}) => {
  const [selectedRole, setSelectedRole] = React.useState(currentRole);
  const [error, setError] = React.useState<string | null>(null);
  const updateRole = useUpdateUserRole(accountId);

  React.useEffect(() => {
    setSelectedRole(currentRole);
    setError(null);
  }, [currentRole, open]);

  const handleConfirm = () => {
    setError(null);
    updateRole.mutate(
      { userId, role: selectedRole },
      {
        onSuccess: () => {
          onClose();
        },
        onError: (err: any) => {
          const detail =
            err?.response?.data?.detail ||
            err?.response?.data?.errors?.[0]?.detail ||
            'Failed to update role';
          setError(detail);
        },
      },
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Change Role</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <DialogContentText sx={{ mb: 2 }}>
          Change the role for <strong>{displayName}</strong>.
        </DialogContentText>
        <FormControl fullWidth size="small">
          <InputLabel>Role</InputLabel>
          <Select
            value={selectedRole}
            label="Role"
            onChange={(e) => setSelectedRole(e.target.value)}
          >
            {Object.entries(ROLE_LABELS).map(([value, label]) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={updateRole.isPending || selectedRole === currentRole}
        >
          {updateRole.isPending ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChangeRoleDialog;
